import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";

// Actions
import { getCampaignOverview } from "../../../actions/campaign-report/CampaignReportActions";
import {
  closeDownloadCampaignGraphReportForm,
  downloadCampaignGraphPdfReport,
  getCampaignMediaDemographicStats,
  openDownloadCampaignGraphReportForm,
} from "../../../actions/graph-reports/CampaignGraphReportActions";

// Custom Hooks
import { useCampaignGraphPdfReportDownload } from "../../../hooks/ReportsDownloadHooks";

// Utils and Constants
import { MetricKeys } from "../GraphReportsConstants";
import { FormDataTargets, India } from "../../../constants/GeneralConstants";
import { checkIsCustomTgPresent } from "../../../utils/CampaignCustomTgUtils";

// Components
import Spinner from "../../../components/spinner/Spinner";
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import DownloadReportsForm from "../../../components/download-reports-from/DownloadReportsForm";

// Page Components
import CampaignMetricsAndMapSection from "./CampaignMetricsAndMapSection";
import CampaignImpressionsSplitSection from "./CampaignImpressionsSplitSection";
import CampaignReachSplitSection from "./CampaignReachSplitSection";
import CampaignMediaGenderRatioAgeSplitSection from "./CampaignMediaGenderRatioAgeSplitSection";
import CampaignOverviewTable from "./CampaignOverviewTable";
import CampaignGraphReportOtherCitiesInfoForm from "./CampaignGraphReportOtherCitiesInfoForm";

// Download Graph Report
function DownloadGraphReport() {
  const dispatch = useDispatch();

  // Selector
  const downloadGraphReportLoading = useSelector(
    (state) => state.campaignGraphReport.downloadCampaignGraphPdfReportLoading
  );

  // Checks for downloading report
  if (downloadGraphReportLoading) {
    return <Spinner />;
  }

  return (
    <p
      className="text-primary cursor-pointer my-2"
      data-toggle="modal"
      data-target={`#${FormDataTargets.downloadReportsForm}`}
      onClick={() => dispatch(openDownloadCampaignGraphReportForm())}
      data-html2canvas-ignore="true"
    >
      Download Graph Report
    </p>
  );
}

/**
 * Campaign Graph Report Page
 */
function CampaignGraphReportPage() {
  const dispatch = useDispatch();
  const { campaignId } = useParams();

  // States
  const [selectedMetric, setSelectedMetric] = useState(MetricKeys.impressions);
  const [selectedCityId, setSelectedCityId] = useState("");
  const [mapCenter, setMapCenter] = useState(India.mapCenter);
  const [mapZoom, setMapZoom] = useState(India.mapZoomLevel5);

  // Selector
  const { campaign, summary } = useSelector(
    (state) => state.campaignReport.campaignSummary
  );
  const { title = "" } = campaign || {};
  const campaignSummaryLoading = useSelector(
    (state) => state.campaignReport.campaignSummaryLoading
  );

  // Campaign Media Demographic stats
  const campaignCityWiseMedias = useSelector(
    (state) => state.campaignGraphReport.campaignCityWiseMedias
  );
  const campaignMediaDemographicStatsLoading = useSelector(
    (state) => state.campaignGraphReport.campaignMediaDemographicStatsLoading
  );

  // form
  const openDownloadCampaignGraphReportModal = useSelector(
    (state) => state.campaignGraphReport.openDownloadCampaignGraphReportModal
  );

  // other cities info form
  const openCampaignGraphReportOtherCitiesInfoModal = useSelector(
    (state) =>
      state.campaignGraphReport.openCampaignGraphReportOtherCitiesInfoModal
  );

  // Dispatch
  useEffect(() => {
    // overview
    dispatch(getCampaignOverview(campaignId));

    // demographic stats for media
    dispatch(getCampaignMediaDemographicStats(campaignId));
  }, [dispatch, campaignId]);

  // pdf report download effects
  useCampaignGraphPdfReportDownload(title);

  // Checks for page loading
  if (campaignSummaryLoading || campaignMediaDemographicStatsLoading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  // Checks for campaign and summary
  if (!campaign || !summary) {
    return null;
  }

  // data
  const { infoByCity, mediaCount, targetGroupId } = campaign;
  const isMediaExist = Boolean(mediaCount);

  // custom tg
  const isCustomTgSelected = checkIsCustomTgPresent(campaign);

  // page actions
  const pageActions = <DownloadGraphReport />;

  function handleDownloadReportSubmit(isSentViaEmail, emailIds) {
    dispatch(
      downloadCampaignGraphPdfReport(
        campaignId,
        selectedCityId,
        selectedMetric,
        mapCenter,
        mapZoom,
        isSentViaEmail,
        emailIds
      )
    );
  }

  return (
    <div className="content-wrapper">
      {/* Page Header */}
      <PageHeader title="Campaign Graph Report" actions={pageActions} />

      {/* Page Contents */}
      <div className="page-content">
        {/* Metrics and Map Section */}
        <CampaignMetricsAndMapSection
          selectedMetric={selectedMetric}
          setSelectedMetric={setSelectedMetric}
          mapCenter={mapCenter}
          setMapCenter={setMapCenter}
          mapZoom={mapZoom}
          setMapZoom={setMapZoom}
        />

        {isMediaExist && (
          <>
            {/* impressions split section */}
            <CampaignImpressionsSplitSection
              infoByCity={infoByCity}
              targetGroupId={targetGroupId}
              isCustomTgSelected={isCustomTgSelected}
            />

            {/* reach split section */}
            <CampaignReachSplitSection
              infoByCity={infoByCity}
              targetGroupId={targetGroupId}
              isCustomTgSelected={isCustomTgSelected}
            />

            {/* Media gender-ratio and age-split section */}
            <CampaignMediaGenderRatioAgeSplitSection
              campaignCityWiseMedias={campaignCityWiseMedias}
              selectedCityId={selectedCityId}
              setSelectedCityId={setSelectedCityId}
            />

            {/* Campaign Overview Table */}
            <CampaignOverviewTable infoByCity={infoByCity} />
          </>
        )}
      </div>

      {/* MODALS */}
      {openDownloadCampaignGraphReportModal && (
        <DownloadReportsForm
          handleSubmit={handleDownloadReportSubmit}
          handleClose={() => dispatch(closeDownloadCampaignGraphReportForm())}
        />
      )}

      {/* pie chart other cities info modal */}
      {openCampaignGraphReportOtherCitiesInfoModal && (
        <CampaignGraphReportOtherCitiesInfoForm />
      )}
    </div>
  );
}

export default CampaignGraphReportPage;
